import { PureComponent } from 'react'
import * as cn from 'classnames'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { StatusType } from 'interfaces'
import * as styles from 'components/features/pfm/HorizontalBarChart/HorizontalBarChart.css'

export type HorizontalBarProps = {
  value: number
  maxValue: number
  isCurrency?: boolean
  kind: StatusType
  maxWidth: number | null
}

type HorizontalBarState = {
  isLabelOutside: boolean
}

export const MINIMUM_BAR_WIDTH_PERCENT = .5
const barLabelMargin = parseInt(styles.barLabelMarginRight, 10) * 2

export class HorizontalBar extends PureComponent<HorizontalBarProps, HorizontalBarState> {
  private labelNode: HTMLSpanElement | null
  private barWidthPercent: number

  constructor (props: HorizontalBarProps) {
    super(props)
    this.setBarWidthPercent()
    this.state = {
      isLabelOutside: this.barWidthPercent <= MINIMUM_BAR_WIDTH_PERCENT,
    }
  }

  render () {
    const {
      value,
      kind,
      isCurrency,
    } = this.props

    const className = styles[kind]
    const displayValue = isCurrency ? formatUSCurrency(value) : value
    const labelClass = cn(styles.barLabel, {
      [styles.outerLabel]: this.state.isLabelOutside,
      [styles.zeroValue]: this.barWidthPercent <= MINIMUM_BAR_WIDTH_PERCENT,
    })

    return (
      <div
        style={{ width: this.barWidthPercent + '%' }}
        className={ cn(styles.bar, className) }
      >
        <span className={ labelClass } ref={ (el) => this.labelNode = el }>
          { displayValue }
        </span>
      </div>
    )
  }

  componentDidMount () {
    if (!this.state.isLabelOutside) {
      this.checkLabelOverflow()
    }
  }

  UNSAFE_componentWillReceiveProps (nextProps: HorizontalBarProps) {
    if (
      nextProps.maxValue !== this.props.maxValue
      || nextProps.value !== this.props.value
      || nextProps.maxWidth !== this.props.maxWidth
    ) {
      this.setBarWidthPercent(nextProps)
      this.checkLabelOverflow(nextProps.maxWidth)
    }
  }

  private setBarWidthPercent = (props = this.props) => {
    const { value, maxValue } = props
    const length = value / maxValue * 100

    if (value <= 0 || length <= MINIMUM_BAR_WIDTH_PERCENT) {
      this.barWidthPercent = MINIMUM_BAR_WIDTH_PERCENT
    } else {
      this.barWidthPercent = maxValue === value ? 100 : length
    }
  }

  private checkLabelOverflow = (maxWidth: HorizontalBarProps['maxWidth'] = this.props.maxWidth) => {
    const { labelNode } = this

    if (!labelNode || !maxWidth) {
      return
    }

    const labelWidth = labelNode.getBoundingClientRect().width
    const isLabelOutside =
      this.barWidthPercent <= MINIMUM_BAR_WIDTH_PERCENT
      || (labelWidth + barLabelMargin) > this.barWidthPercent / 100 * maxWidth

    if (isLabelOutside !== this.state.isLabelOutside) {
      this.setState({ isLabelOutside })
    }
  }
}

export default HorizontalBar
